import ConversationItemSkeleton from '@/components/messaging/ConversationItemSkeleton';
import ConversationList from '@/components/messaging/ConversationList';
import MessagingNav from '@/components/messaging/MessagingNav';
import SentBatchList from '@/components/messaging/sent/SentBatchList';
import { SearchInput } from '@/components/ui/search-input';
import {
  getConversationsFromPages,
  useConversationsQuery,
} from '@/hooks/queries/use-conversations-query';
import { Box, Flex, Text } from '@chakra-ui/react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/auth-context';
import { parseAsString, parseAsStringLiteral, useQueryState } from 'nuqs';
import SidebarPanelHeader from '../SidebarPanelHeader';

interface ConversationsMenuProps {
  onBack?: () => void;
}

const ConversationsMenu: React.FC<ConversationsMenuProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useQueryState('search', parseAsString.withDefault(''));
  const [currentFilter] = useQueryState(
    'messageFilter',
    parseAsStringLiteral(['inbox', 'sent', 'archived'] as const).withDefault('inbox')
  );
  const prevFilterRef = useRef(currentFilter);
  const conversationScrollRef = useRef<HTMLDivElement>(null);

  const isSentTab = currentFilter === 'sent';
  const conversationStatus =
    currentFilter === 'archived' ? ('archived' as const) : ('active' as const);

  useEffect(() => {
    if (prevFilterRef.current === currentFilter) return;
    prevFilterRef.current = currentFilter;
    if (searchQuery) void setSearchQuery(null);
  }, [currentFilter, searchQuery, setSearchQuery]);

  const searchPlaceholder =
    currentFilter === 'sent'
      ? t('messages.search.placeholderSent')
      : currentFilter === 'archived'
        ? t('messages.search.placeholderArchived')
        : t('messages.search.placeholderInbox');

  const {
    data: conversationsData,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useConversationsQuery(user?.currentBrandId, {
    search: searchQuery || undefined,
    status: conversationStatus,
    enabled: !isSentTab,
  });
  const conversations = useMemo(
    () => getConversationsFromPages(conversationsData?.pages),
    [conversationsData?.pages]
  );

  const handleLoadMoreConversations = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleSearchChange = (value: string) => {
    void setSearchQuery(value || null);
  };

  return (
    <Flex direction="column" h="full" overflow="hidden">
      <Box px={3} pt={4} pb={3}>
        <SidebarPanelHeader title={t('navigation.inbox')} onBack={onBack} />
        <Box mt={4}>
          <SearchInput
            w="full"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder={searchPlaceholder}
            size="sm"
            tone="dark"
          />
        </Box>
        <Box mt={4}>
          <MessagingNav />
        </Box>
      </Box>

      <Box
        ref={conversationScrollRef}
        flex={1}
        overflowY="auto"
        px={3}
        pt={3}
        pb={3}
        borderTop="1px solid"
        borderColor="navDark.footerBorder"
      >
        {isSentTab ? (
          <SentBatchList searchQuery={searchQuery || undefined} />
        ) : isLoading && conversations.length === 0 ? (
          <ConversationItemSkeleton />
        ) : error ? (
          <Text color="navDark.dangerText" p={4} fontSize="sm" textAlign="center">
            {error?.message || t('messages.errorLoading')}
          </Text>
        ) : conversations.length === 0 ? (
          <Flex justify="center" align="center" h="200px">
            <Text color="navDark.textSubtle" fontSize="sm">
              {t('messages.noConversations')}
            </Text>
          </Flex>
        ) : (
          <ConversationList
            conversations={conversations}
            searchQuery={searchQuery}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            loadMoreRootRef={conversationScrollRef}
            onLoadMore={handleLoadMoreConversations}
          />
        )}
      </Box>
    </Flex>
  );
};

export default ConversationsMenu;
